import React, { useState, useRef } from 'react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import ImageCropModal from './ImageCropModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploadField({
  label,
  value,
  onChange,
  uploadEndpoint,
  placeholder,
  previewShape = 'rounded',  // 'rounded' for profile, 'square' for logo
  testId,
  compact = false,
  cropToSquare = false,      // open a crop/zoom step before upload (profile photo)
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cropFile, setCropFile] = useState(null);  // file awaiting crop
  const fileInputRef = useRef(null);

  // Entry point for any chosen file. For profile photos we route through the
  // crop/zoom modal first so the user picks the focal area; everything else
  // uploads directly.
  const receiveFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('imageUpload.invalidType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageUpload.tooLarge'));
      return;
    }
    if (cropToSquare) {
      setCropFile(file);
    } else {
      handleUpload(file);
    }
  };

  const handleCropConfirm = (blob) => {
    setCropFile(null);
    if (!blob) return;
    // Wrap the cropped blob as a File so the existing upload path is unchanged.
    const cropped = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
    handleUpload(cropped);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('imageUpload.invalidType'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageUpload.tooLarge'));
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
      };
      const gatewayKey = process.env.REACT_APP_GATEWAY_KEY;
      if (gatewayKey) {
        headers['X-Gateway-Key'] = gatewayKey;
      }
      const response = await fetch(`${API_BASE}/api/${uploadEndpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('imageUpload.uploadFailed'));
      }

      const data = await response.json();
      onChange(data.url);
      toast.success(t('imageUpload.uploadSuccess'));
    } catch (error) {
      toast.error(error.message || t('imageUpload.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) receiveFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) receiveFile(file);
    // Reset so selecting the same file again re-triggers onChange.
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
    toast.success(t('imageUpload.removed'));
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="text-sm font-medium text-[#23211a] block">
          {label}
        </label>
      )}

      {/* Upload Zone */}
      <div
        data-testid={testId ? `${testId}-dropzone` : undefined}
        className={`
          relative border-2 border-dashed rounded-xl text-center transition-all duration-200
          ${compact ? 'p-4' : 'p-6'}
          ${dragOver
            ? 'border-[#1f4d3a] bg-[#e9efe7]'
            : 'border-[#d8d0bd] hover:border-[#1f4d3a]/40 bg-[#f7f3ea]/30'
          }
          ${uploading ? 'pointer-events-none opacity-60' : 'cursor-pointer'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          data-testid={testId ? `${testId}-input` : undefined}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
            <span className="text-sm text-[#6f6a5c]">{t('imageUpload.uploading')}</span>
          </div>
        ) : value ? (
          <div className="flex items-center justify-center gap-4">
            <div className="relative">
              <img
                src={value}
                alt="Preview"
                className={`
                  ${previewShape === 'rounded'
                    ? 'w-20 h-20 rounded-full'
                    : 'h-16 max-w-32'
                  }
                  object-cover border-2 border-[#1f4d3a]/50
                `}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute -top-2 -right-2 p-1 bg-[#b3543f] rounded-full hover:bg-[#a34430] transition-colors"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
            <div className="text-left">
              <p className="text-sm text-[#4a463b]">{t('imageUpload.uploaded')}</p>
              <p className="text-xs text-[#8a8474]">{t('imageUpload.clickOrDragReplace')}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className={`${compact ? 'p-2' : 'p-3'} bg-[#e9e2d2] rounded-full`}>
              <Upload className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-[#6f6a5c]`} />
            </div>
            <div>
              <p className="text-sm text-[#4a463b]">{t('imageUpload.dragOrClick')}</p>
              <p className="text-xs text-[#8a8474] mt-1">{t('imageUpload.maxSize')}</p>
            </div>
          </div>
        )}
      </div>

      {/* URL Input (alternative) */}
      {!compact && (
        <>
          <div className="relative">
            <Input
              data-testid={testId ? `${testId}-url` : undefined}
              type="url"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] pr-10"
            />
            <ImageIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a8474]" />
          </div>
          <p className="text-xs text-[#8a8474]">{t('imageUpload.orPasteUrl')}</p>
        </>
      )}

      {cropFile && (
        <ImageCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
}
